import { Component, For, Show, createMemo } from "solid-js";
import type { WorkItemType, WorkItemTypeId } from "../../../api";

export interface TypeSelectorProps {
  /** Available work item types to choose from */
  types: WorkItemType[];
  /** Currently selected type ID */
  selectedType: WorkItemTypeId | undefined;
  /** AI-suggested type ID (from enrichment) */
  aiSuggestedType?: WorkItemTypeId;
  /** Whether AI is currently analyzing the input */
  isAnalyzing?: boolean;
  /** Disabled state (e.g., during form submission) */
  disabled?: boolean;
  /** Callback when user selects a type */
  onSelect: (typeId: WorkItemTypeId) => void;
}

/**
 * TypeSelector - Visual type selection with AI suggestion support
 *
 * States:
 * - idle: Shows type buttons, no selection or analyzing
 * - analyzing: Shows type buttons with shimmer overlay while AI determines type
 * - ai-selected: Type selected by AI, shows the mono `ai` tag
 * - user-selected: User manually selected a type, no `ai` tag
 *
 * Accessibility:
 * - Uses radiogroup pattern for screen readers
 * - Keyboard shortcuts 1-4 for quick type selection
 * - Live region announces AI classification
 */
export const TypeSelector: Component<TypeSelectorProps> = (props) => {
  const isAiSelected = (typeId: WorkItemTypeId) =>
    typeId === props.aiSuggestedType && typeId === props.selectedType;

  const aiAnnouncement = createMemo(() => {
    if (props.aiSuggestedType && props.selectedType === props.aiSuggestedType) {
      const typeName = props.types.find(
        (t) => t.id === props.aiSuggestedType,
      )?.name;
      return typeName ? `AI suggests ${typeName}` : "";
    }
    return "";
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.disabled || props.isAnalyzing) return;

    const num = parseInt(e.key);
    if (num >= 1 && num <= 4) {
      const targetType = props.types[num - 1];
      if (targetType) {
        e.preventDefault();
        props.onSelect(targetType.id);
      }
    }
  };

  return (
    <div
      class="grid grid-cols-2 sm:grid-cols-4 gap-1"
      role="radiogroup"
      aria-label="Work item type"
      tabIndex={props.disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
    >
      {/* Type buttons - always visible, with shimmer effect when analyzing */}
      <For each={props.types}>
        {(type, index) => {
          const isSelected = () => props.selectedType === type.id;
          const showAiTag = () => isAiSelected(type.id);

          return (
            <button
              type="button"
              role="radio"
              aria-checked={isSelected()}
              disabled={props.disabled}
              class={`relative inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium rounded-sm border transition-colors focus-ring ${
                isSelected()
                  ? "bg-ink text-paper border-ink"
                  : "bg-surface border-line text-ink-2 hover:bg-sunken hover:text-ink"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={() => props.onSelect(type.id)}
              title={`${type.name}${showAiTag() ? " (AI suggested)" : ""} - Press ${index() + 1} to select`}
            >
              {type.name}
              <Show when={showAiTag()}>
                <span
                  class="font-mono text-[9px] uppercase tracking-[0.12em] opacity-70"
                  aria-hidden="true"
                >
                  ai
                </span>
              </Show>
              {/* Shimmer overlay when analyzing */}
              <Show when={props.isAnalyzing}>
                <div
                  class="absolute inset-0 rounded-sm bg-gradient-to-r from-transparent via-surface/80 to-transparent animate-shimmer"
                  style={{ "background-size": "400% 100%" }}
                />
              </Show>
            </button>
          );
        }}
      </For>

      {/* Screen reader announcement for AI classification */}
      <span class="sr-only" aria-live="polite">
        {aiAnnouncement()}
      </span>
    </div>
  );
};

export default TypeSelector;
